import { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import LQIP from "../assets/forest_lqip.jpg";
import HI_WEBP from "../assets/forest_image-1920.webp";
import MID_WEBP from "../assets/forest_image-1280.webp";
import HI_JPG from "../assets/forest_image.jpg"; // fallback universal

const Wrap = styled.div`
  position: fixed;
  inset: 0;
  z-index: 0;
  background: #2d4425; /* cor dominante para 1º paint */
`;

const Layer = styled.div`
  position: absolute;
  inset: 0;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: ${({ $url }) => `url("${$url}")`};
  filter: ${({ $crisp }) => ($crisp ? "blur(0)" : "blur(12px)")};
  opacity: ${({ $crisp }) => ($crisp ? 1 : 0.96)};
  transition: filter 300ms ease, opacity 300ms ease;
  will-change: filter, opacity;
`;

function canUseWebP() {
  try {
    return document
      .createElement("canvas")
      .toDataURL("image/webp")
      .startsWith("data:image/webp");
  } catch {
    return false;
  }
}

function pickTarget({ preferReducedData }) {
  // Nunca retorna null. Em dados reduzidos usa médio (ou JPG).
  const webp = canUseWebP();
  const hi = webp ? HI_WEBP : HI_JPG;
  const mid = webp ? MID_WEBP : HI_JPG;
  return preferReducedData ? mid : hi;
}

export default function LoginBg() {
  const preferReducedData = useMemo(() => {
    const c =
      typeof navigator !== "undefined" &&
      (navigator.connection ||
        navigator.webkitConnection ||
        navigator.mozConnection);
    const is2G = c?.effectiveType?.includes("2g");
    const saveData = c?.saveData === true;
    return Boolean(is2G || saveData);
  }, []);

  const target = useMemo(
    () => pickTarget({ preferReducedData }),
    [preferReducedData]
  );

  const [url, setUrl] = useState(LQIP);
  const [crisp, setCrisp] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Safety timeout: se nada carregar em 6s, tira o blur do LQIP
    const safety = setTimeout(() => {
      if (!cancelled) setCrisp(true);
    }, 6000);

    const img = new Image();

    const finish = (finalUrl) => {
      if (cancelled) return;
      setUrl(finalUrl);
      requestAnimationFrame(() => setCrisp(true));
      clearTimeout(safety);
    };

    img.onload = () => finish(target);

    img.onerror = () => {
      // Falhou (ex.: WebP sem suporte ou 404) → tenta JPG
      if (target !== HI_JPG) {
        const fallback = new Image();
        fallback.onload = () => finish(HI_JPG);
        fallback.onerror = () => {
          // Último recurso: remove blur do LQIP para não ficar eterno
          if (!cancelled) setCrisp(true);
        };
        fallback.src = HI_JPG;
      } else {
        if (!cancelled) setCrisp(true);
      }
    };

    img.src = target;

    return () => {
      cancelled = true;
      clearTimeout(safety);
    };
  }, [target]);

  return (
    <Wrap>
      <Layer $url={url} $crisp={crisp} />
    </Wrap>
  );
}
